import { apiUrl } from "../utils/api";
import { oeffneEventSource } from "../utils/sseUrl";
import { BEARER_TOKEN_KEY } from "../utils/auth-client";

// ── DTOs (mirror apps/api/src/telephony/telephony.dto.ts) ──

export interface ExtensionListItem {
    number: string;
    displayName?: string | null;
    email?: string | null;
    mobileNumber?: string | null;
    presenceStatus?: string | null;
    online: boolean;
    onlineEndpoints: string[];
    assignedUserId?: string | null;
}

export interface ExtensionMappingDto {
    id: number;
    extension: string;
    userId: string;
    displayName?: string | null;
    email?: string | null;
}

export interface MyExtensionDto {
    extension: string;
    displayName?: string | null;
    email?: string | null;
}

export interface AssignExtensionInput {
    extension: string;
    userId: string;
    displayName?: string;
    email?: string;
}

export interface SyncExtensionsResultDto {
    fetchedFromPbx: number;
    matchedByEmail: number;
    upserted: number;
    unmatchedExtensions: string[];
}

export type CallDirection = "inbound" | "outbound" | "internal";

export interface CallLogDto {
    id: number;
    callId: string;
    direction: CallDirection;
    status?: string | null;
    fromNumber?: string | null;
    toNumber?: string | null;
    didNumber?: string | null;
    extension?: string | null;
    userId?: string | null;
    /** Aufgelöster Name des annehmenden/zugeordneten Users (SP1, „Angenommen von"). */
    userName?: string | null;
    contactId?: number | null;
    contactDisplayName?: string | null;
    /** A2: true = zugeordneter Kontakt ist eine Firma → neutrale Anrede „Hallo zusammen," in der Follow-up-Mail. */
    contactIsCompany?: boolean | null;
    /** A2: Vorname des zugeordneten Kontakts (Personen), für „Hallo <Vorname>," in der Follow-up-Mail; null = keiner. */
    contactFirstName?: string | null;
    /** T-12: Kontakt-ID der Firma, für die eine Person anruft; null = keine oder nur Freitext. */
    contactCompanyId?: number | null;
    /** T-12: Firma, für die eine Person anruft (Arbeitgeber-Regel, nur wenn sichtbar); bei Firmen null. */
    contactCompanyName?: string | null;
    /** SP5: Standard-E-Mail des zugeordneten Kontakts (Backend-DTO-Join); Empfänger-Vorbelegung der Follow-up-Mail. */
    contactEmail?: string | null;
    startedAt: string;
    callDurationSec?: number | null;
    talkDurationSec?: number | null;
    notes?: string | null;
    mailLinkId?: number | null;
    /** SP6: zugeordneter Vorgang (A13); null = keiner. */
    vorgangId?: number | null;
    /**
     * Zugeordnetes To-do; null = keins. Die chronologische Vorgangsansicht
     * gruppiert die Zeitachse danach — ohne das Feld landete jeder To-do-Anruf
     * im Archiv statt im To-do, an dem er hängt.
     */
    todoId?: number | null;
    recordingFile?: string | null;
    /** AP10: ISO-Timestamp der Erledigung; null = offener Anruf. */
    resolvedAt?: string | null;
    resolvedByUserId?: string | null;
    /** Aufgelöster Name des erledigenden Users (SP1). */
    resolvedByUserName?: string | null;
    /** Phase 2b: Transkript der Aufzeichnung; null = keins. */
    transcript?: string | null;
    transcriptLang?: string | null;
    transcribedAt?: string | null;
    /**
     * Der Weg des Anrufs: ein Eintrag je Abschnitt, in der Reihenfolge der
     * Anlage. Leer bei Anrufen von vor dem Umbau — die Anzeige muss damit
     * umgehen können.
     */
    legs?: CallLegDto[];
    /** Team, dessen Anruf niemand angenommen hat; null = kein Gruppenanruf. */
    missedGroupNumber?: string | null;
    missedGroupName?: string | null;
    /**
     * T-51: Name der Gegenstelle, wenn sie eine interne Durchwahl ist (Kollege
     * statt „731"). Kein Kontakt — Rückruf und „Kontakt zuordnen“ bleiben an
     * Nummer bzw. `contactId`.
     */
    counterpartyName?: string | null;
    /**
     * Alle Kontakte des Anrufs, Hauptkontakt (= `contactId`) zuerst. Weitere
     * Kontakte nur, soweit der Benutzer sie sehen darf. Fehlt bei älteren
     * Antworten — dann gilt `contactId` allein (siehe `callKontakte.ts`).
     */
    contacts?: CallLogContactRef[];
    /**
     * Alle Vorgänge/To-dos, denen der Anruf zugeordnet ist (PO 01.10.2026),
     * primäre zuerst — nur lesbare Vorgänge. Fehlt bei großen Listen ohne
     * `mitZuordnungen`; dann gelten `vorgangId`/`todoId` (siehe
     * `callZuordnungen.ts`).
     */
    zuordnungen?: CallLogZuordnungRef[];
    /** Weitere Zuordnungen an Vorgängen ohne Leserecht (nur als Zahl). */
    weitereZuordnungenVerborgen?: number;
    /** Nur in der Antwort von `addZuordnungen`: Kontakte, die im Zielvorgang fehlen (F2). */
    fehlendeKontakte?: CallFehlendeKontakte[];
}

/** Eine Zuordnung eines Anrufs zu einem Vorgang bzw. einem To-do darin. */
export interface CallLogZuordnungRef {
    /** Id der Zuordnung — für Lösen/Umhängen. */
    id: number;
    vorgangId: number;
    vorgangNbr: string | null;
    vorgangTitel: string | null;
    /** null = Zuordnung auf Vorgangsebene. */
    todoId: number | null;
    todoTitel: string | null;
    /** Die älteste Zuordnung — gespiegelt in `vorgangId`/`todoId`. */
    istPrimaer: boolean;
}

/** F2: Kontakte des Anrufs, die ein Zielvorgang nicht kennt. */
export interface CallFehlendeKontakte {
    vorgangId: number;
    vorgangTitel: string | null;
    kontakte: Array<{ id: number; displayName: string | null }>;
}

/** Ein Ziel beim Zuordnen: Vorgang (ohne To-dos) oder To-do darin. */
export interface CallZuordnungZiel {
    vorgangId: number;
    todoId?: number | null;
}

/** F3: eindeutig passender aktiver Vorgang des Kontakts samt To-dos. */
export interface CallVorgangVorschlag {
    vorgang: {
        id: number;
        vorgangNbr: string | null;
        titel: string | null;
        todos: Array<{ id: number; titel: string | null; status: string | null }>;
    } | null;
}

/** Ein Kontakt eines Anrufs (ein Anruf kann mehreren Kontakten gehören). */
export interface CallLogContactRef {
    id: number;
    displayName: string | null;
    /** true = Hauptkontakt (CallLog.contactId). */
    isPrimary: boolean;
}

/** Wohin ein Abschnitt ging und was dabei herauskam. */
export interface CallLegDto {
    startedAt: string;
    targetNumber?: string | null;
    targetName?: string | null;
    /** extension | queue | ringgroup | voicemail | external | other */
    targetKind: string;
    disposition?: string | null;
    durationSec?: number | null;
    talkSec?: number | null;
    userId?: string | null;
    userName?: string | null;
}

/** Fortschritt des Reparatur-Imports (Abschnitte für Bestandsanrufe). */
export interface RebuildLegsStatusDto {
    running: boolean;
    tenantId: string;
    von: string;
    bis: string;
    startedAt: string;
    finishedAt?: string | null;
    fensterVon?: string | null;
    fensterBis?: string | null;
    datensaetze: number;
    anrufeBerechnet: number;
    anrufeNeu: number;
    abschnitte: number;
    fehler: number;
    letzterFehler?: string | null;
    abgebrochen: boolean;
    /** Woran der Import gerade arbeitet; null wenn er ruht. */
    schritt?: string | null;
    gruppen: number;
    korrigiert: number;
}

export interface ListCallsResponse {
    calls: CallLogDto[];
    total: number;
}

export interface ListCallsQuery {
    contactId?: number;
    /** SP6: nur Anrufe, die diesem Vorgang zugeordnet sind. */
    vorgangId?: number;
    /** Todo-Detailpage SP1: nur Anrufe, die diesem Todo zugeordnet sind. */
    todoId?: number;
    /** Nur den Anruf mit dieser Gesprächs-ID (SDK/PBX `callId`). */
    callId?: string;
    userId?: string;
    extension?: string;
    from?: string;
    to?: string;
    limit?: number;
    offset?: number;
    /** Sichtbarkeitsbereich (analog Kontakt-Tabelle): Alle / Team / Persönlich. */
    scope?: "all" | "own" | "team";
    /**
     * Suche über die ganze Historie (serverseitig): Kontaktname oder — wenn der
     * Begriff wie eine Nummer aussieht — die Ziffern der Rufnummer.
     */
    search?: string;
    /** Nur offene verpasste Anrufe (Spiegel von `isOpenMissed`). */
    missedOnly?: boolean;
    /** Nur Anrufe ohne Kontakt (weder Haupt- noch weiterer Kontakt). */
    ohneKontakt?: boolean;
    /** Zuordnungen je Anruf mitliefern (bei vorgangId/todoId ohnehin). */
    mitZuordnungen?: boolean;
}

export interface UpdateCallInput {
    notes?: string | null;
    /**
     * Text an die Notiz ANHÄNGEN (atomar im Backend): leere Notiz → wird
     * gesetzt, sonst Leerzeile + „— <Kürzel>, <HH:MM>" + Text. Nicht mit `notes`.
     */
    appendNotes?: string;
    contactId?: number | null;
    mailLinkId?: number | null;
    /**
     * @deprecated Seit 01.10.2026 kann ein Anruf an mehreren Vorgängen hängen —
     * `addZuordnungen`/`removeZuordnung`/`moveZuordnung` nutzen. Der PATCH ist
     * im Backend nur noch ein Adapter (hinzufügen statt ersetzen).
     *
     * SP6: Anruf einem Vorgang zuordnen (id) oder lösen (null).
     *
     * `null` wirkt erst seit dem 14.09.2026: bis dahin machte die DTO des
     * Backends aus `null` ein `undefined`, und der Service ließ das Feld
     * dann in Ruhe — „lösen“ war ein stiller No-op mit 200 OK.
     */
    vorgangId?: number | null;
    /**
     * @deprecated siehe `vorgangId`.
     *
     * Anruf einem To-do zuordnen (id) oder lösen (null). Das Backend nimmt das
     * Feld längst an (`UpdateCallLogDto.todoId`, tenant-scoped validiert) — im
     * Frontend-Typ fehlte es nur, weshalb es niemand nutzen konnte. Für `null`
     * gilt dieselbe Einschränkung wie bei `vorgangId`.
     */
    todoId?: number | null;
    /** AP10: true = erledigen, false = wieder öffnen. */
    resolved?: boolean;
}

// ── Schnellwahl: Kontakt-Suche nach Name ODER Nummer (mirror DialTargetDto) ──

export interface DialNumber {
    /** Label/Typ der Nummer (phone/mobile bzw. Freitext-Label). */
    label?: string | null;
    /** Telefonnummer wie gespeichert. */
    value: string;
    /** Kommunikationstyp: phone | mobile. */
    type: string;
    /** true = Standard-Nummer dieses Typs. */
    isDefault: boolean;
}

export interface DialTarget {
    contactId: number;
    displayName: string;
    /** T-12: Firma einer Person (gleiche Regel wie CallLogDto.contactCompanyName). */
    companyName?: string | null;
    numbers: DialNumber[];
}

// ── Voicemail (mirror apps/api/src/telephony/telephony.dto.ts) ──

export interface VoicemailItem {
    msgId: string;
    uid?: string | null;
    fromNumber?: string | null;
    fromName?: string | null;
    durationSec?: number | null;
    sizeBytes?: number | null;
    /** PBX-local timestamp string (YYYY/MM/DD HH:MM:SS). */
    time?: string | null;
    isRead: boolean;
    fileName?: string | null;
    /**
     * T-192: der Anruf (CallLog), der in dieser Nachricht endete — daran hängt
     * die Vorgangszuordnung. null, solange der Anruf noch nicht synchronisiert ist.
     */
    callLogId?: number | null;
    /** T-192: Gesprächs-ID dieses Anrufs (zum Nachladen über `listCalls({ callId })`). */
    callId?: string | null;
    /** T-192: An wie vielen Vorgängen/To-dos der Anruf hängt. */
    anzahlZuordnungen?: number;
}

export interface VoicemailListResponse {
    voicemails: VoicemailItem[];
    total: number;
}

// ── SSE event envelope (mirror backend) ──

/** 'ping' = Heartbeat des SSE-Streams (leere callId), hält Proxies offen. */
export type TelephonyEventKind = "incoming" | "status" | "ended" | "cdr" | "ping" | "uebergabe";

export interface TelephonyEvent {
    kind: TelephonyEventKind;
    tenantId: string;
    userId: string;
    callId: string;
    subStatus?: string;
    payload?: {
        extension?: string | null;
        fromNumber?: string | null;
        toNumber?: string | null;
        contactId?: number | null;
        contactDisplayName?: string | null;
        /** Richtung laut Anlage — bei 'incoming' gesetzt (auch ausgehend am Tischtelefon). */
        direction?: "inbound" | "outbound" | "internal";
        callLogId?: number | null;
        startedAt?: string;
        /** Nur bei `uebergabe`: die Notiz des Kollegen, der weiterleitet. */
        notiz?: string;
        vonName?: string | null;
    };
}

// ── Call-Widget: Kontakt + FO/BO-Auflösung (P5, mirror ResolveResultDto) ──

export interface ResolveOwnersResult {
    contactId: number;
    /** Anzeigename des Kontakts („Nachname, Vorname"). */
    displayName: string;
    foUserId?: string | null;
    /** Anzeigename des Frontoffice-Users (Kontaktverantwortlicher). */
    foName?: string | null;
    /** Backoffice-Typ: NONE | USER | TEAM. */
    boType: string;
    /** Anzeigename des Backoffice (User- bzw. Team-Name); null bei NONE. */
    boName?: string | null;
}

// ── Linkus Web SDK (Phase 3) ──

export interface SdkCredentials {
    username: string;
    extension: string;
    secret: string;
    pbxURL: string;
    expiresAt: string;
}

// ── service ──

async function ok<T>(res: Response, what: string): Promise<T> {
    if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`${what} fehlgeschlagen (${res.status}): ${txt}`);
    }
    return res.json() as Promise<T>;
}

function qparams(q: ListCallsQuery): Record<string, string | number> {
    const out: Record<string, string | number> = {};
    if (q.contactId !== undefined) out.contactId = q.contactId;
    if (q.vorgangId !== undefined) out.vorgangId = q.vorgangId;
    if (q.todoId !== undefined) out.todoId = q.todoId;
    if (q.userId) out.userId = q.userId;
    // Ohne diese Zeile fiel der Filter still weg und `listCalls({ callId })`
    // lieferte einfach den neuesten Anruf der Liste.
    if (q.callId) out.callId = q.callId;
    if (q.extension) out.extension = q.extension;
    if (q.from) out.from = q.from;
    if (q.to) out.to = q.to;
    if (q.limit !== undefined) out.limit = q.limit;
    if (q.offset !== undefined) out.offset = q.offset;
    if (q.scope) out.scope = q.scope;
    if (q.search && q.search.trim()) out.search = q.search.trim();
    if (q.missedOnly) out.missedOnly = "true";
    if (q.ohneKontakt) out.ohneKontakt = "true";
    if (q.mitZuordnungen) out.mitZuordnungen = "true";
    return out;
}

/**
 * Fenster-Ereignis „Anrufe wurden erledigt" — das Anruf-Badge der Navigation
 * (`useNavBadges`) zählt dann sofort neu statt erst im nächsten 60-s-Takt.
 * Feuert z. B. der Abschnitt „Verpasster Anruf" der Kontaktliste.
 */
export const ANRUFE_GEAENDERT = "kmvs:anrufe-geaendert";

export const telephonyService = {
    listExtensions(): Promise<ExtensionListItem[]> {
        return fetch(apiUrl("telephony/extensions")).then((r) => ok(r, "Extension-Liste"));
    },
    listMappings(): Promise<ExtensionMappingDto[]> {
        return fetch(apiUrl("telephony/mappings")).then((r) => ok(r, "Mapping-Liste"));
    },
    myExtension(): Promise<MyExtensionDto | null> {
        return fetch(apiUrl("telephony/me")).then(async (r) => {
            if (r.status === 404) return null;
            return ok<MyExtensionDto>(r, "Meine Extension");
        });
    },
    assignExtension(input: AssignExtensionInput): Promise<ExtensionMappingDto> {
        return fetch(apiUrl("telephony/extensions/assign"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(input),
        }).then((r) => ok(r, "Extension zuweisen"));
    },
    removeMapping(id: number): Promise<void> {
        return fetch(apiUrl(`telephony/mappings/${id}`), { method: "DELETE" }).then((r) => {
            if (!r.ok) throw new Error(`Mapping entfernen fehlgeschlagen (${r.status})`);
        });
    },
    syncExtensions(): Promise<SyncExtensionsResultDto> {
        return fetch(apiUrl("telephony/extensions/sync"), { method: "POST" }).then((r) =>
            ok(r, "Extension-Sync"),
        );
    },
    /**
     * Startet den Reparatur-Import der Anruf-Abschnitte. Kehrt SOFORT zurück —
     * der Lauf dauert länger als jede HTTP-Antwort; der Fortschritt kommt über
     * {@link rebuildLegsStatus}.
     */
    startRebuildLegs(body: { from?: string; to?: string } = {}): Promise<RebuildLegsStatusDto> {
        return fetch(apiUrl("telephony/calls/rebuild-legs"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        }).then((r) => ok(r, "Reparatur-Import starten"));
    },
    /** Fortschritt des Reparatur-Imports; null = es lief noch keiner. */
    rebuildLegsStatus(): Promise<RebuildLegsStatusDto | null> {
        return fetch(apiUrl("telephony/calls/rebuild-legs")).then((r) =>
            ok(r, "Fortschritt des Reparatur-Imports"),
        );
    },
    /** Stoppt den laufenden Import nach dem aktuellen Anruf. */
    stopRebuildLegs(): Promise<RebuildLegsStatusDto | null> {
        return fetch(apiUrl("telephony/calls/rebuild-legs"), { method: "DELETE" }).then((r) =>
            ok(r, "Reparatur-Import stoppen"),
        );
    },
    /**
     * Hakt verpasste Anrufe stapelweise ab. Das Backend trifft dabei nur, was
     * der Anmeldende auch einzeln erledigen dürfte — `scope` schränkt weiter
     * ein, erweitert aber nie.
     */
    resolveMissedBulk(body: {
        before?: string;
        scope?: "all" | "own" | "team";
    }): Promise<{ erledigt: number }> {
        return fetch(apiUrl("telephony/calls/resolve-missed"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        }).then((r) => ok(r, "Verpasste Anrufe erledigen"));
    },
    listCalls(query: ListCallsQuery = {}, init?: { signal?: AbortSignal }): Promise<ListCallsResponse> {
        return fetch(apiUrl("telephony/calls", qparams(query)), init).then((r) => ok(r, "Anrufliste"));
    },
    /**
     * Schnellwahl-Suche: Kontakte nach Name ODER Telefonnummer-Ziffern (RBAC-scoped,
     * SP1-Endpoint). Leerer Begriff liefert serverseitig []. Name matcht ab 1 Zeichen,
     * Nummern-Suffix ab 3 Ziffern.
     */
    dialSearch(q: string, opts: { limit?: number; signal?: AbortSignal } = {}): Promise<DialTarget[]> {
        const term = q.trim();
        if (!term) return Promise.resolve([]);
        const params: Record<string, string | number> = { q: term };
        if (opts.limit !== undefined) params.limit = opts.limit;
        return fetch(apiUrl("telephony/dial-search", params), { signal: opts.signal }).then((r) =>
            ok(r, "Schnellwahl-Suche"),
        );
    },
    /**
     * Call-Widget (P5): zu einer Telefonnummer den zugeordneten Kontakt samt
     * FO/BO-Verantwortlichkeit (mit Anzeigenamen) auflösen. RBAC-/tenant-scoped;
     * liefert `null`, wenn keine bekannte Nummer matcht oder der Kontakt nicht
     * sichtbar ist. Best-effort: Fehler werden zu `null` geschluckt (kein Crash
     * im Anruf-Flow).
     */
    resolveOwners(number: string): Promise<ResolveOwnersResult | null> {
        const n = number.trim();
        if (!n) return Promise.resolve(null);
        return fetch(apiUrl("telephony/resolve", { number: n }))
            .then((r) => (r.ok ? (r.json() as Promise<ResolveOwnersResult | null>) : null))
            .catch(() => null);
    },
    /**
     * Eine Rufnummer einem Kontakt zuschlagen. Das Backend zieht dabei ALLE
     * bisher kontaktlosen Anrufe dieser Nummer nach — nicht nur den aktuellen.
     * Best effort: Scheitert das, ist die eigentliche Zuordnung trotzdem
     * passiert (die Nummer hängt am Kontakt), nur der Verlauf bleibt ungeändert.
     */
    linkNumberToContact(number: string, contactId: number): Promise<{ verknuepft: number }> {
        const n = number.trim();
        if (!n) return Promise.resolve({ verknuepft: 0 });
        return fetch(apiUrl("telephony/numbers/link"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ number: n, contactId }),
        })
            .then((r) => (r.ok ? (r.json() as Promise<{ verknuepft: number }>) : { verknuepft: 0 }))
            .catch(() => ({ verknuepft: 0 }));
    },
    updateCall(id: number, patch: UpdateCallInput): Promise<CallLogDto> {
        return fetch(apiUrl(`telephony/calls/${id}`), {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(patch),
        }).then((r) => ok(r, "Anruf-Update"));
    },
    /**
     * Einen (weiteren) Kontakt an den Anruf hängen. `primary` macht ihn zum
     * Hauptkontakt; ein Anruf ohne Kontakt bekommt den ersten automatisch als
     * Hauptkontakt. Die Nummer wird dabei NICHT an den Kontakt geschrieben.
     */
    addCallContact(id: number, contactId: number, opts: { primary?: boolean } = {}): Promise<CallLogDto> {
        return fetch(apiUrl(`telephony/calls/${id}/contacts`), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ contactId, ...(opts.primary ? { primary: true } : {}) }),
        }).then((r) => ok(r, "Kontakt hinzufügen"));
    },
    /**
     * Den Anruf weiteren Vorgängen/To-dos zuordnen (hinzufügen, idempotent).
     * `resolve` stempelt einen offenen Anruf einmalig als erledigt. Die
     * Antwort trägt `fehlendeKontakte` (F2).
     */
    addZuordnungen(id: number, ziele: CallZuordnungZiel[], opts: { resolve?: boolean } = {}): Promise<CallLogDto> {
        return fetch(apiUrl(`telephony/calls/${id}/zuordnungen`), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                ziele: ziele.map((z) => ({ vorgangId: z.vorgangId, todoId: z.todoId ?? null })),
                ...(opts.resolve ? { resolve: true } : {}),
            }),
        }).then((r) => ok(r, "Vorgang zuordnen"));
    },
    /** Eine Zuordnung lösen; andere Zuordnungen des Anrufs bleiben. */
    removeZuordnung(id: number, zuordnungId: number): Promise<CallLogDto> {
        return fetch(apiUrl(`telephony/calls/${id}/zuordnungen/${zuordnungId}`), {
            method: "DELETE",
        }).then((r) => ok(r, "Zuordnung lösen"));
    },
    /** Eine Zuordnung im selben Vorgang umhängen (anderes To-do oder `null` = Vorgang). */
    moveZuordnung(id: number, zuordnungId: number, todoId: number | null): Promise<CallLogDto> {
        return fetch(apiUrl(`telephony/calls/${id}/zuordnungen/${zuordnungId}`), {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ todoId }),
        }).then((r) => ok(r, "Zuordnung umhängen"));
    },
    /** F3: Vorschlag „passender aktiver Vorgang samt To-dos" für die Nach-Anruf-Karte. */
    vorgangVorschlag(id: number): Promise<CallVorgangVorschlag> {
        return fetch(apiUrl(`telephony/calls/${id}/vorgang-vorschlag`)).then((r) =>
            ok(r, "Vorgangsvorschlag"),
        );
    },
    /** Kontakt vom Anruf lösen; beim Hauptkontakt rückt der nächste nach. */
    removeCallContact(id: number, contactId: number): Promise<CallLogDto> {
        return fetch(apiUrl(`telephony/calls/${id}/contacts/${contactId}`), {
            method: "DELETE",
        }).then((r) => ok(r, "Kontakt entfernen"));
    },
    /** AP10: Anruf als erledigt markieren (setzt resolvedAt serverseitig). */
    resolveCall(id: number): Promise<CallLogDto> {
        return fetch(apiUrl(`telephony/calls/${id}/resolve`), {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
        }).then((r) => ok(r, "Anruf erledigen"));
    },
    /**
     * SDK-getriebener CallLog-Eintrag: das Softphone meldet einen Anruf EINMAL
     * bei Gesprächsende mit finalem Status + Dauer. Läuft parallel zum
     * CDR-Webhook (Backend upsertet auf (tenantId, callId)); Best-effort, der
     * Webhook bleibt Fallback.
     */
    ingestCall(input: {
        callId: string;
        direction: "inbound" | "outbound" | "internal";
        fromNumber?: string;
        toNumber?: string;
        status?: string;
        startedAt?: string;
        callDurationSec?: number;
        talkDurationSec?: number;
        extension?: string;
    }): Promise<{ callLogId: number }> {
        return fetch(apiUrl("telephony/calls/ingest"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(input),
        }).then((r) => ok(r, "Anruf-Ingestion"));
    },
    // ── voicemail (Yeastar vm/* proxy) ──
    listVoicemails(page = 1, pageSize = 50): Promise<VoicemailListResponse> {
        return fetch(apiUrl("telephony/voicemails", { page, pageSize })).then((r) =>
            ok(r, "Voicemail-Liste"),
        );
    },
    /**
     * Fetch the voicemail audio as a Blob via the (bearer-patched) fetch, so the
     * caller can play it through an object URL — the <audio> element itself can't
     * send the Authorization header.
     */
    async fetchVoicemailAudio(msgId: string): Promise<Blob> {
        const res = await fetch(apiUrl(`telephony/voicemails/${encodeURIComponent(msgId)}/audio`));
        if (!res.ok) throw new Error(`Voicemail-Audio fehlgeschlagen (${res.status})`);
        return res.blob();
    },
    markVoicemailRead(msgId: string): Promise<{ ok: true }> {
        return fetch(apiUrl(`telephony/voicemails/${encodeURIComponent(msgId)}/read`), {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
        }).then((r) => ok(r, "Voicemail als gelesen markieren"));
    },
    deleteVoicemail(msgId: string): Promise<void> {
        return fetch(apiUrl(`telephony/voicemails/${encodeURIComponent(msgId)}`), {
            method: "DELETE",
        }).then((r) => {
            if (!r.ok) throw new Error(`Voicemail löschen fehlgeschlagen (${r.status})`);
        });
    },
    // ── call recording (recording/* proxy) ──
    /**
     * Fetch a call's recording audio as a Blob via the (bearer-patched) fetch,
     * for playback through an object URL (the <audio> element can't send the
     * Authorization header). 404 → null when the call has no recording.
     */
    async fetchCallRecordingAudio(callId: number): Promise<Blob | null> {
        const res = await fetch(apiUrl(`telephony/calls/${callId}/recording/audio`));
        if (res.status === 404) return null;
        if (!res.ok) throw new Error(`Aufzeichnung fehlgeschlagen (${res.status})`);
        return res.blob();
    },
    // ── transcription (Phase 2b, config-gated) ──
    transcriptionStatus(): Promise<{ enabled: boolean }> {
        return fetch(apiUrl("telephony/transcription/status")).then((r) =>
            ok(r, "Transkriptions-Status"),
        );
    },
    transcribeCall(callId: number): Promise<CallLogDto> {
        return fetch(apiUrl(`telephony/calls/${callId}/transcribe`), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
        }).then((r) => ok(r, "Transkription"));
    },
    // ── Linkus Web SDK softphone (Phase 3, feature-flagged) ──
    sdkStatus(): Promise<{ enabled: boolean }> {
        return fetch(apiUrl("telephony/sdk/status")).then((r) => ok(r, "SDK-Status"));
    },
    sdkCredentials(): Promise<SdkCredentials> {
        return fetch(apiUrl("telephony/sdk-credentials")).then((r) => ok(r, "SDK-Credentials"));
    },
    /** Phase 5: transcribe one live-call audio chunk (base64). */
    transcribeLive(audioBase64: string): Promise<{ text: string; language?: string | null }> {
        return fetch(apiUrl("telephony/transcribe-live"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ audioBase64 }),
        }).then((r) => ok(r, "Live-Transkription"));
    },
    /**
     * Open the SSE stream. EventSource cannot set Authorization headers, so the
     * bearer is passed as ?access_token=… when available.
     *
     * SSO-Fix (wie realtimeService): Microsoft-SSO-Sessions haben KEINEN Bearer
     * in localStorage — EventSource sendet same-origin-Cookies, die Cookie-
     * Session authentifiziert. Ohne Token also OHNE access_token verbinden
     * statt still gar nicht (SSO-User hatten sonst keine Anruf-Popups).
     */
    /**
     * Die laufenden Gespräche des Nutzers laut Anlage — für das Fenster nach
     * einem Neuladen (dieselbe Form wie die `incoming`-Ereignisse des Stroms).
     */
    async aktiveAnrufe(): Promise<TelephonyEvent[]> {
        const res = await fetch(apiUrl("telephony/aktive-anrufe"));
        if (!res.ok) throw new Error(`Laufende Gespräche laden fehlgeschlagen: ${res.status}`);
        return res.json();
    },
    /** Notiz an den Kollegen, an den weitergeleitet wird (`uebergabeNotiz.ts`). */
    async uebergabeNotiz(body: { ziel: string; anruferNummer: string | null; text: string }): Promise<{ zugestellt: boolean }> {
        const res = await fetch(apiUrl("telephony/uebergabe-notiz"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(`Notiz-Übergabe fehlgeschlagen: ${res.status}`);
        return res.json();
    },
    openEventStream(): EventSource | null {
        const token = localStorage.getItem(BEARER_TOKEN_KEY);
        return oeffneEventSource(
            token ? apiUrl("telephony/events", { access_token: token }) : apiUrl("telephony/events"),
        );
    },
};
